// ---------------------------------------------------------------------------
// P1c — toques brutos e detector de "saltos" no arrasto (só diagnóstico, só com "?dbg=1"). NÃO altera comportamento.
//
// T1  Ouvinte na fase de CAPTURA da janela, passivo e só de leitura (sem preventDefault, sem stopPropagation, sem mexer
//     em touch-action): anota cada pointerdown/move/up/cancel (ponteiro, tipo, posição em px CSS, alvo, botões, eventos
//     agrupados) num anel de 400 eventos em arrays tipados. O alvo vira um índice numa tabela pequena de rótulos
//     (canvas, rótulo do botão, outro); a tabela só cresce na 1ª vez que um elemento aparece.
// T2  Por quadro em arrasto: referência do tratador (última posição do dedo usada como base), posição do 1º toque do
//     traço, alvo no início do traço, dono do gesto (activeGesture) e ponteiros ativos.
// T3  Detector de SALTO: pedido do dedo (velas) >= max(120, 5× a mediana dos últimos 60 quadros de arrasto). Guarda
//     tudo que separa as hipóteses (segundo dedo, referência não reiniciada, gesto, eventos agrupados) + os últimos
//     30 eventos brutos.
// T5  Down/up em botões da interface (anel próprio), para correlacionar com os saltos (inclui os "Marcar").
// Zero alocação por quadro e por evento no JS do app. Única exceção: o array que o NAVEGADOR devolve em
// getCoalescedEvents() (só em pointermove) — não há como pedir a contagem sem ele.
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';
import { navRec } from './navRecorder';

const f = (x: number, d = 1) => (isFinite(x) ? x.toFixed(d) : '—');

const RAW_CAP = 400, JUMP_CAP = 100, SNAP_N = 30, BTN_CAP = 120, FR_CAP = 240, MAXP = 8, MAXL = 40, MED_N = 60;
const JUMP_MIN = 120, JUMP_X = 5;
/** ganho do arrasto do tempo (velas por px; o mesmo do tratador em SceneContent) e tolerância da validação */
const DRAG_GAIN = 1, PED_DIFF_PX = 2;
const DU_WINDOW_MS = 150, BTN_WINDOW_MS = 1200;
export const T_DOWN = 0, T_MOVE = 1, T_UP = 2, T_CANCEL = 3;
const T_CH = ['d', 'm', 'u', 'c'];
const PT_NAMES = ['mouse', 'pen', 'touch', 'outro'];
const TG_OUTRO = 0, TG_CANVAS = 1;

/** Cópia mínima do que se lê de um PointerEvent (permite testar sem navegador). */
export interface PtrEv {
  pointerId: number; pointerType: string; isPrimary: boolean; clientX: number; clientY: number; timeStamp: number; buttons: number;
  target: EventTarget | null; getCoalescedEvents?: () => unknown[];
}

export class TouchRecorder {
  // ---------------------------------------------------------------- rótulos de alvo
  readonly labels: string[] = ['outro', 'canvas'];
  private tgtCache = new WeakMap<object, number>();
  private labelIdx(s: string): number {
    const i = this.labels.indexOf(s);
    if (i >= 0) return i;
    if (this.labels.length >= MAXL) return TG_OUTRO;
    this.labels.push(s); return this.labels.length - 1;
  }
  private targetOf(t: EventTarget | null): number {
    if (!t) return TG_OUTRO;
    const o = t as object, hit = this.tgtCache.get(o);
    if (hit !== undefined) return hit;
    let idx = TG_OUTRO;
    const el = t as any;
    if (typeof HTMLCanvasElement !== 'undefined' && el instanceof HTMLCanvasElement) idx = TG_CANVAS;
    else if (typeof el.closest === 'function') {
      const b = el.closest('button,[role="button"],a');
      if (b) {
        const raw = String(b.getAttribute?.('aria-label') || b.textContent || '').trim().replace(/\s*\(\d+\)\s*$/, '');
        idx = this.labelIdx(raw.slice(0, 24) || 'botão');
      }
    }
    this.tgtCache.set(o, idx);
    return idx;
  }
  private gestures: string[] = ['—'];
  private gestureIdx(g: string | null | undefined): number {
    if (!g) return 0;
    const i = this.gestures.indexOf(g);
    if (i >= 0) return i;
    if (this.gestures.length >= 16) return 0;
    this.gestures.push(g); return this.gestures.length - 1;
  }

  // ---------------------------------------------------------------- T1: eventos brutos (anel de 400)
  rawN = 0; private rawHead = 0;
  rT = new Float64Array(RAW_CAP); rType = new Uint8Array(RAW_CAP); rPid = new Int32Array(RAW_CAP); rPt = new Uint8Array(RAW_CAP);
  rPrim = new Uint8Array(RAW_CAP); rX = new Float32Array(RAW_CAP); rY = new Float32Array(RAW_CAP); rTgt = new Uint8Array(RAW_CAP);
  rBtn = new Uint8Array(RAW_CAP); rCo = new Uint8Array(RAW_CAP);
  // ponteiros ativos (pressionados)
  private aId = new Int32Array(MAXP); private aX = new Float32Array(MAXP); private aY = new Float32Array(MAXP); nActive = 0;
  private slotOf(id: number): number { for (let i = 0; i < this.nActive; i++) if (this.aId[i] === id) return i; return -1; }
  // último down/up de qualquer ponteiro (anel de 8): instante e ponteiro
  private duT = new Float64Array(8); private duPid = new Int32Array(8); private duHead = 0; private duN = 0;
  // último movimento (para o detector)
  lmT = 0; lmPrevT = 0; lmPid = -1; lmTgt = 0; lmDxOwn = 0; lmDyOwn = 0; lmDxAny = 0; lmDyAny = 0; lmCo = 1;
  private lmX = NaN; private lmY = NaN;
  // início do traço (1º down no canvas com nenhum outro ponteiro ativo)
  strokeX0 = NaN; strokeTgt0 = NaN; strokePid = -1;

  onPointer(e: PtrEv, type: number): void {
    const id = e.pointerId, tgt = this.targetOf(e.target), now = e.timeStamp;
    let slot = this.slotOf(id);
    let co = 1;
    if (type === T_DOWN) {
      if (slot < 0) {
        if (this.nActive === MAXP) { // tabela cheia (up perdido): descarta o mais antigo
          for (let i = 1; i < MAXP; i++) { this.aId[i - 1] = this.aId[i]; this.aX[i - 1] = this.aX[i]; this.aY[i - 1] = this.aY[i]; }
          this.nActive--;
        }
        slot = this.nActive++; this.aId[slot] = id;
      }
      if (tgt === TG_CANVAS && this.nActive === 1) { this.strokeX0 = e.clientX; this.strokeTgt0 = navRec ? navRec.targetNow : NaN; this.strokePid = id; this.firstPending = true; }
      this.aX[slot] = e.clientX; this.aY[slot] = e.clientY;
      this.duPush(now, id);
    } else if (type === T_MOVE) {
      if (slot < 0) { if (e.buttons === 0) return; slot = this.nActive < MAXP ? this.nActive++ : MAXP - 1; this.aId[slot] = id; this.aX[slot] = e.clientX; this.aY[slot] = e.clientY; } // move sem down visto: segue
      this.lmPrevT = this.lmT; this.lmT = now; this.lmPid = id; this.lmTgt = tgt;
      this.lmDxOwn = e.clientX - this.aX[slot]; this.lmDyOwn = e.clientY - this.aY[slot];
      this.lmDxAny = this.lmX === this.lmX ? e.clientX - this.lmX : 0; this.lmDyAny = this.lmY === this.lmY ? e.clientY - this.lmY : 0;
      this.lmX = e.clientX; this.lmY = e.clientY;
      this.aX[slot] = e.clientX; this.aY[slot] = e.clientY;
      if (typeof e.getCoalescedEvents === 'function') { try { co = e.getCoalescedEvents().length; } catch { co = 1; } }
      this.lmCo = co;
    } else { // up / cancel
      this.duPush(now, id);
      if (slot >= 0) { // remoção por troca com o último
        const last = this.nActive - 1;
        this.aId[slot] = this.aId[last]; this.aX[slot] = this.aX[last]; this.aY[slot] = this.aY[last];
        this.nActive = last;
      }
    }
    const i = this.rawHead; this.rawHead = (i + 1) % RAW_CAP; this.rawN++;
    this.rT[i] = now; this.rType[i] = type; this.rPid[i] = id;
    this.rPt[i] = e.pointerType === 'mouse' ? 0 : e.pointerType === 'pen' ? 1 : e.pointerType === 'touch' ? 2 : 3;
    this.rPrim[i] = e.isPrimary ? 1 : 0; this.rX[i] = e.clientX; this.rY[i] = e.clientY; this.rTgt[i] = tgt;
    this.rBtn[i] = e.buttons > 255 ? 255 : e.buttons; this.rCo[i] = co > 255 ? 255 : co;
    if (tgt > TG_CANVAS && type !== T_MOVE) this.btnPush(now, type, tgt, id); // T5
  }
  private duPush(t: number, pid: number): void { const i = this.duHead; this.duHead = (i + 1) % 8; this.duT[i] = t; this.duPid[i] = pid; this.duN++; }

  // ---------------------------------------------------------------- T5: botões da interface
  btnN = 0; private btnHead = 0;
  bT = new Float64Array(BTN_CAP); bType = new Uint8Array(BTN_CAP); bLab = new Uint8Array(BTN_CAP); bPid = new Int32Array(BTN_CAP);
  readonly btnDown = new Float64Array(MAXL);
  private btnPush(t: number, type: number, lab: number, pid: number): void {
    const i = this.btnHead; this.btnHead = (i + 1) % BTN_CAP; this.btnN++;
    this.bT[i] = t; this.bType[i] = type; this.bLab[i] = lab; this.bPid[i] = pid;
    if (type === T_DOWN) this.btnDown[lab]++;
  }

  // ---------------------------------------------------------------- T2: estado do tratador, por quadro
  refBox: { current: number | null } | null = null;
  /** O tratador de cada ponteiro-move chama aqui (depois de applyTimeDrag): referência antes/depois e pedido (velas). */
  tdN = 0; private dragLastX = NaN; private dragLastRefA = NaN;
  frN = 0; private frHead = 0;
  fT = new Float64Array(FR_CAP); fRef = new Float32Array(FR_CAP); fX = new Float32Array(FR_CAP); fX0 = new Float32Array(FR_CAP);
  fTgt0 = new Float32Array(FR_CAP); fGest = new Uint8Array(FR_CAP); fNp = new Uint8Array(FR_CAP); fPed = new Float32Array(FR_CAP);
  private medBuf = new Float32Array(MED_N).fill(Infinity); private medHead = 0; private medCount = 0; private medScr = new Float32Array(MED_N);
  med = 0;
  /** Um chamado por quadro (rAF do painel), depois de navRec.frame. `mode` 1 = arrasto manual; `pedCandles` = pedido do dedo no quadro. */
  frame(now: number, mode: number, gesture: string | null, pedCandles: number): void {
    if (mode !== 1) return;
    const ped = pedCandles < 0 ? -pedCandles : pedCandles;
    const i = this.frHead; this.frHead = (i + 1) % FR_CAP; this.frN++;
    const r = this.refBox ? this.refBox.current : null;
    this.fT[i] = now; this.fRef[i] = r === null ? NaN : r; this.fX[i] = this.dragLastX; this.fX0[i] = this.strokeX0; this.fTgt0[i] = this.strokeTgt0;
    this.fGest[i] = this.gestureIdx(gesture); this.fNp[i] = this.nActive; this.fPed[i] = ped;
    // mediana dos últimos 60 quadros de arrasto (ordenação in-place de um buffer fixo; os vazios são +Infinito)
    this.medBuf[this.medHead] = ped; this.medHead = (this.medHead + 1) % MED_N; if (this.medCount < MED_N) this.medCount++;
    this.medScr.set(this.medBuf); this.medScr.sort();
    this.med = this.medScr[(this.medCount - 1) >> 1];
  }

  // ---------------------------------------------------------------- T3: detector de saltos
  jumpN = 0; private jHead = 0; jumpMax = 0;
  /** P2a: movimentos aplicados com a referência vazia (meta 0) e, entre eles, os que eram o 1º movimento do traço */
  emptyRefN = 0; emptyRefFirstN = 0; private firstPending = false;
  /** P2a: maior |pedido/ganho − deslocamento do dedo| (px) e quantos movimentos passaram de 2 px */
  maxPedDiffPx = 0; pedDiffN = 0;
  jT = new Float64Array(JUMP_CAP); jPed = new Float32Array(JUMP_CAP); jDx = new Float32Array(JUMP_CAP); jDy = new Float32Array(JUMP_CAP);
  jDxAny = new Float32Array(JUMP_CAP); jDyAny = new Float32Array(JUMP_CAP); jMsMove = new Float32Array(JUMP_CAP);
  jMsDU = new Float32Array(JUMP_CAP); jMsDUo = new Float32Array(JUMP_CAP); jNp = new Uint8Array(JUMP_CAP); jPid = new Int32Array(JUMP_CAP);
  jTgt = new Uint8Array(JUMP_CAP); jGest = new Uint8Array(JUMP_CAP); jRefB = new Float32Array(JUMP_CAP); jRefA = new Float32Array(JUMP_CAP);
  jX = new Float32Array(JUMP_CAP); jX0 = new Float32Array(JUMP_CAP); jTg0 = new Float32Array(JUMP_CAP); jTgB = new Float32Array(JUMP_CAP);
  jTgA = new Float32Array(JUMP_CAP); jCo = new Uint8Array(JUMP_CAP); jThr = new Float32Array(JUMP_CAP);
  sRel = new Float32Array(JUMP_CAP * SNAP_N); sType = new Uint8Array(JUMP_CAP * SNAP_N); sPid = new Int16Array(JUMP_CAP * SNAP_N);
  sX = new Float32Array(JUMP_CAP * SNAP_N); sY = new Float32Array(JUMP_CAP * SNAP_N); sTgt = new Uint8Array(JUMP_CAP * SNAP_N);
  sCo = new Uint8Array(JUMP_CAP * SNAP_N); sLen = new Uint8Array(JUMP_CAP);
  // contagens
  readonly byNp = new Float64Array(2);            // 1 ponteiro | >= 2
  readonly byDU = new Float64Array(2);            // houve down/up de OUTRO ponteiro em <= 150 ms: não | sim
  readonly byTgt = new Float64Array(MAXL);
  private pxAbs = new Float32Array(JUMP_CAP);     // |ΔX| por salto (para a mediana, em ordem de chegada)

  /**
   * Chamado pelos tratadores de toque do tempo, DEPOIS de applyTimeDrag: `clientX` do evento, referência antes/depois
   * (px), pedido em velas (já com o ganho). Só lê e anota.
   */
  drag(now: number, clientX: number, pid: number, refBefore: number | null, refAfter: number | null, pedCandles: number, gesture: string | null): void {
    this.tdN++;
    // P2a (validação): referência vazia no 1º movimento aplicado do traço; pedido × deslocamento do dedo (ganho 1 vela/px)
    const emptyRef = refBefore === null || !(refBefore === refBefore);
    if (emptyRef) this.emptyRefN++;
    if (this.firstPending) { this.firstPending = false; if (emptyRef) this.emptyRefFirstN++; }
    if (this.lmPid === pid) {
      const d = pedCandles / DRAG_GAIN - this.lmDxOwn, ad = d < 0 ? -d : d;
      if (ad > this.maxPedDiffPx) this.maxPedDiffPx = ad;
      if (ad > PED_DIFF_PX) this.pedDiffN++;
    }
    this.dragLastX = clientX; this.dragLastRefA = refAfter === null ? NaN : refAfter;
    const a = pedCandles < 0 ? -pedCandles : pedCandles;
    const thr = JUMP_X * this.med > JUMP_MIN ? JUMP_X * this.med : JUMP_MIN;
    if (a < thr) return;
    const i = this.jHead; this.jHead = (i + 1) % JUMP_CAP; this.jumpN++;
    if (a > this.jumpMax) this.jumpMax = a;
    const tgtA = navRec ? navRec.targetNow : NaN;
    this.jT[i] = now; this.jPed[i] = pedCandles; this.jDx[i] = this.lmDxOwn; this.jDy[i] = this.lmDyOwn;
    this.jDxAny[i] = this.lmDxAny; this.jDyAny[i] = this.lmDyAny;
    this.jMsMove[i] = this.lmPrevT > 0 ? this.lmT - this.lmPrevT : NaN;
    // desde o último down/up (qualquer ponteiro) e desde o último de OUTRO ponteiro que não o do movimento
    let any = NaN, oth = NaN;
    const n = this.duN < 8 ? this.duN : 8;
    for (let k = 1; k <= n; k++) {
      const q = (this.duHead - k + 16) % 8, dt = this.lmT - this.duT[q];
      if (any !== any) any = dt;
      if (oth !== oth && this.duPid[q] !== this.lmPid) { oth = dt; break; }
    }
    this.jMsDU[i] = any; this.jMsDUo[i] = oth;
    this.jNp[i] = this.nActive; this.jPid[i] = pid; this.jTgt[i] = this.lmTgt; this.jGest[i] = this.gestureIdx(gesture);
    this.jRefB[i] = refBefore === null ? NaN : refBefore; this.jRefA[i] = refAfter === null ? NaN : refAfter; this.jX[i] = clientX;
    this.jX0[i] = this.strokeX0; this.jTg0[i] = this.strokeTgt0; this.jTgA[i] = tgtA; this.jTgB[i] = navRec ? tgtA - navRec.lastApl : NaN;
    this.jCo[i] = this.lmCo; this.jThr[i] = thr;
    // últimos 30 eventos brutos
    const len = this.rawN < SNAP_N ? this.rawN : SNAP_N, o = i * SNAP_N;
    for (let k = 0; k < len; k++) {
      const r = (this.rawHead - len + k + RAW_CAP * 2) % RAW_CAP, j = o + k;
      this.sRel[j] = this.rT[r] - this.lmT; this.sType[j] = this.rType[r]; this.sPid[j] = this.rPid[r]; this.sX[j] = this.rX[r];
      this.sY[j] = this.rY[r]; this.sTgt[j] = this.rTgt[r]; this.sCo[j] = this.rCo[r];
    }
    this.sLen[i] = len;
    // contagens
    this.byNp[this.nActive >= 2 ? 1 : 0]++;
    const duo = this.jMsDUo[i]; this.byDU[duo === duo && duo <= DU_WINDOW_MS ? 1 : 0]++;
    this.byTgt[this.lmTgt]++;
    this.pxAbs[(this.jumpN - 1) % JUMP_CAP] = this.lmDxOwn < 0 ? -this.lmDxOwn : this.lmDxOwn;
  }

  // ---------------------------------------------------------------- relatórios (fora do caminho quente)
  private rawStr(j: number): string {
    const t = this.sType[j];
    return `${f(this.sRel[j], 0)}${T_CH[t]}${this.sPid[j]}@${f(this.sX[j], 0)},${f(this.sY[j], 0)}${this.sCo[j] > 1 ? '*' + this.sCo[j] : ''}${this.sTgt[j] === TG_CANVAS ? '' : `{${this.labels[this.sTgt[j]]}}`}`;
  }
  private nearestBtn(t: number): string {
    const sz = Math.min(this.btnN, BTN_CAP); let best = Infinity, bi = -1;
    for (let k = 0; k < sz; k++) {
      const i = (this.btnHead - sz + k + BTN_CAP * 2) % BTN_CAP;
      if (this.bType[i] !== T_DOWN) continue;
      const d = this.bT[i] - t;
      if (Math.abs(d) < Math.abs(best)) { best = d; bi = i; }
    }
    return bi >= 0 && Math.abs(best) <= BTN_WINDOW_MS ? `${this.labels[this.bLab[bi]]} ${best >= 0 ? '+' : ''}${f(best / 1000, 2)}s` : '';
  }
  private jumpLine(i: number, rel: (t: number) => string, withRaw: number): string {
    const btn = this.nearestBtn(this.jT[i]);
    let s = `t=${rel(this.jT[i])} ${this.jPed[i] >= 0 ? '+' : ''}${f(this.jPed[i], 0)} velas | ΔX ${f(this.jDx[i], 0)} ΔY ${f(this.jDy[i], 0)}px (vs qualquer ponteiro: ${f(this.jDxAny[i], 0)},${f(this.jDyAny[i], 0)}) | desde o movimento anterior ${f(this.jMsMove[i], 0)}ms | desde down/up: qualquer ${f(this.jMsDU[i], 0)}ms, de outro ponteiro ${f(this.jMsDUo[i], 0)}ms | ponteiros ${this.jNp[i]} | id ${this.jPid[i]} alvo ${this.labels[this.jTgt[i]]} agrupados ${this.jCo[i]} | gesto ${this.gestures[this.jGest[i]]} | referência ${f(this.jRefB[i], 0)}→${f(this.jRefA[i], 0)}px (dedo em ${f(this.jX[i], 0)}; 1º toque do traço em ${f(this.jX0[i], 0)}) | alvo ${f(this.jTgB[i], 0)}→${f(this.jTgA[i], 0)} (no início do traço ${f(this.jTg0[i], 0)}) | botão mais próximo: ${btn || 'nenhum em ±1,2s'}`;
    if (withRaw > 0) {
      const len = this.sLen[i], o = i * SNAP_N, parts: string[] = [];
      for (let k = Math.max(0, len - withRaw); k < len; k++) parts.push(this.rawStr(o + k));
      s += `\n   brutos (ms antes do salto, d/m/u/c, ponteiro, x,y, *agrupados): ${parts.join(' ')}`;
    }
    return s;
  }
  /** SALTOS DO ARRASTO. `examples`: quantos saltos listar; `raw`: quantos eventos brutos por salto. */
  jumpsReport(t0Abs: number, examples: number, raw: number): string[] {
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sz = Math.min(this.jumpN, JUMP_CAP);
    const px: number[] = []; for (let k = 0; k < sz; k++) px.push(this.pxAbs[k]);
    px.sort((a, b) => a - b);
    const medPx = px.length ? px[(px.length - 1) >> 1] : NaN;
    L.push(`=== SALTOS DO ARRASTO: ${this.jumpN} | maior ${f(this.jumpMax, 0)} velas | mediana ${f(medPx, 0)} px/salto | limiar: max(${JUMP_MIN} velas, ${JUMP_X}× a mediana dos últimos ${MED_N} quadros; agora ${f(Math.max(JUMP_MIN, JUMP_X * this.med), 0)}) | toques registrados ${this.rawN} ===`);
    const tg: string[] = []; for (let k = 0; k < MAXL; k++) if (this.byTgt[k] > 0) tg.push(`${this.labels[k]} ${this.byTgt[k]}`);
    let withBtn = 0; for (let k = 0; k < sz; k++) if (this.nearestBtn(this.jT[(this.jHead - sz + k + JUMP_CAP * 2) % JUMP_CAP])) withBtn++;
    L.push(`referência vazia no 1º movimento: ${this.emptyRefFirstN} | em qualquer movimento: ${this.emptyRefN} | maior |pedido − deslocamento do dedo × ganho|: ${f(this.maxPedDiffPx, 1)} px (movimentos acima de ${PED_DIFF_PX} px: ${this.pedDiffN} de ${this.tdN})`);
    L.push(`ponteiros ativos: 1 → ${this.byNp[0]} | ≥ 2 → ${this.byNp[1]} | down/up de OUTRO ponteiro em ≤ ${DU_WINDOW_MS}ms: sim ${this.byDU[1]} | não ${this.byDU[0]} | alvo do último movimento: ${tg.join(' | ') || '—'} | botão tocado em ±1,2s: ${withBtn}`);
    for (let q = Math.max(0, sz - examples); q < sz; q++) L.push(this.jumpLine((this.jHead - sz + q + JUMP_CAP * 2) % JUMP_CAP, rel, raw));
    return L;
  }
  /** Toques em botões da interface (T5). */
  buttonsReport(t0Abs: number, lines: number): string[] {
    const L: string[] = [];
    const sz = Math.min(this.btnN, BTN_CAP);
    const c: string[] = []; for (let k = 2; k < MAXL; k++) if (this.btnDown[k] > 0) c.push(`${this.labels[k]} ${this.btnDown[k]}`);
    L.push(`=== TOQUES EM BOTÕES: ${this.btnN} eventos | toques por botão: ${c.join(' | ') || '—'} ===`);
    for (let q = Math.max(0, sz - lines); q < sz; q++) {
      const i = (this.btnHead - sz + q + BTN_CAP * 2) % BTN_CAP;
      L.push(`t=${f((this.bT[i] - t0Abs) / 1000, 2)}s ${this.bType[i] === T_DOWN ? 'down' : this.bType[i] === T_UP ? 'up' : 'cancel'} ${this.labels[this.bLab[i]]} ponteiro ${this.bPid[i]}`);
    }
    return L;
  }
  /** Estado do tratador, por quadro em arrasto (T2) — log completo. */
  stateReport(t0Abs: number, lines: number): string[] {
    const L: string[] = [];
    const sz = Math.min(this.frN, FR_CAP);
    L.push(`=== ESTADO DO TRATADOR DO ARRASTO (últimos ${Math.min(lines, sz)} quadros em arrasto de ${this.frN}; px = clientX) ===`);
    L.push('t | referência(px) | último x usado(px) | 1º toque do traço(px) | alvo no início do traço(velas) | gesto | ponteiros | pedido do quadro(velas)');
    for (let q = Math.max(0, sz - lines); q < sz; q++) {
      const i = (this.frHead - sz + q + FR_CAP * 2) % FR_CAP;
      L.push(`${f((this.fT[i] - t0Abs) / 1000, 2)}s | ${f(this.fRef[i], 0)} | ${f(this.fX[i], 0)} | ${f(this.fX0[i], 0)} | ${f(this.fTgt0[i], 0)} | ${this.gestures[this.fGest[i]]} | ${this.fNp[i]} | ${f(this.fPed[i], 1)}`);
    }
    return L;
  }
  /** Últimos toques brutos (log completo). */
  rawReport(t0Abs: number, lines: number): string[] {
    const L: string[] = [];
    const sz = Math.min(this.rawN, RAW_CAP);
    L.push(`=== TOQUES BRUTOS (últimos ${Math.min(lines, sz)} de ${this.rawN}; tipo d/m/u/c, ponteiro, tipo do ponteiro, primário, x,y px CSS, alvo, botões, agrupados) ===`);
    for (let q = Math.max(0, sz - lines); q < sz; q++) {
      const i = (this.rawHead - sz + q + RAW_CAP * 2) % RAW_CAP;
      L.push(`${f((this.rT[i] - t0Abs) / 1000, 3)}s ${T_CH[this.rType[i]]} id${this.rPid[i]} ${PT_NAMES[this.rPt[i]]}${this.rPrim[i] ? ' prim' : ''} ${f(this.rX[i], 0)},${f(this.rY[i], 0)} ${this.labels[this.rTgt[i]]} b${this.rBtn[i]}${this.rCo[i] > 1 ? ' *' + this.rCo[i] : ''}`);
    }
    return L;
  }
}

export const touchRec: TouchRecorder | null = DBG_ENABLED ? new TouchRecorder() : null;

/** Liga o ouvinte na captura da janela (passivo, só leitura). Devolve a função que desliga. Sem a flag: nada. */
export function installTouchListeners(): () => void {
  if (!DBG_ENABLED || typeof window === 'undefined') return () => {};
  const R = touchRec!;
  const opt = { capture: true, passive: true } as const;
  const down = (e: Event) => R.onPointer(e as PointerEvent, T_DOWN);
  const move = (e: Event) => R.onPointer(e as PointerEvent, T_MOVE);
  const up = (e: Event) => R.onPointer(e as PointerEvent, T_UP);
  const cancel = (e: Event) => R.onPointer(e as PointerEvent, T_CANCEL);
  window.addEventListener('pointerdown', down, opt); window.addEventListener('pointermove', move, opt);
  window.addEventListener('pointerup', up, opt); window.addEventListener('pointercancel', cancel, opt);
  return () => {
    window.removeEventListener('pointerdown', down, opt); window.removeEventListener('pointermove', move, opt);
    window.removeEventListener('pointerup', up, opt); window.removeEventListener('pointercancel', cancel, opt);
  };
}

/** Os tratadores de toque do tempo (SceneContent) informam cada movimento aplicado. Sem a flag: nada. */
export function touchDrag(clientX: number, pointerId: number, refBefore: number | null, refAfter: number | null, pedCandles: number, gesture: string | null): void {
  if (DBG_ENABLED) touchRec!.drag(performance.now(), clientX, pointerId, refBefore, refAfter, pedCandles, gesture);
}
